import { Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FilterProvider } from "./context/FilterContext";
import Comercial from "./pages/Comercial";
import Financiero from "./pages/Financiero";
import Operaciones from "./pages/Operaciones";
import Admin from "./pages/Admin";
import AdminUsuarios from "./pages/AdminUsuarios";
import Login from "./pages/Login";
import ThemeToggle from "./components/ThemeToggle";
import { PALETTE } from "./lib/palette";

const linkBase = "px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 no-underline";

function estiloLink(isActive: boolean) {
  return {
    background: isActive ? PALETTE.primaryDark : "transparent",
    color: isActive ? "#fff" : undefined,
  };
}

/** Pantalla neutra mientras se consulta /me, para no parpadear el login. */
function Cargando() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-sm opacity-70">Cargando…</p>
    </div>
  );
}

/**
 * Envoltorio de rutas privadas.
 *
 * Hasta que `listo` es false no se decide nada: si se redirigiera antes de
 * conocer la sesion, cada recarga expulsaria al usuario al login.
 */
function RequiereLogin({ children }: { children: React.ReactNode }) {
  const { user, listo } = useAuth();
  const location = useLocation();

  if (!listo) return <Cargando />;
  if (!user) {
    const destino = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(destino)}`} replace />;
  }
  return <>{children}</>;
}

/** Solo admin: la gestion de usuarios y cargas es de administracion. */
function RequiereAdmin({ children }: { children: React.ReactNode }) {
  const { user, listo, esAdmin } = useAuth();
  if (!listo) return <Cargando />;
  if (!user) return <Navigate to="/login" replace />;
  if (!esAdmin) {
    return (
      <div className="p-6">
        <h2 className="text-lg font-semibold mb-2">Acceso restringido</h2>
        <p className="text-sm opacity-80">
          Esta sección es solo para administradores. Tu usuario tiene el rol{" "}
          <strong>{user.role}</strong>.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

function Layout() {
  const location = useLocation();
  const { user, esAdmin, logout } = useAuth();

  return (
    <FilterProvider>
      <div className="app">
        <header className="app-header">
          <h1>
            <img src="/favicon-32x32.png" alt="" width={32} height={32} className="brand-icon" />
            Restaurant Dashboards
          </h1>
          <nav className="flex gap-2 items-center">
            <NavLink to={{ pathname: "/comercial", search: location.search }} className={linkBase} style={({ isActive }) => estiloLink(isActive)}>Comercial</NavLink>
            <NavLink to={{ pathname: "/financiero", search: location.search }} className={linkBase} style={({ isActive }) => estiloLink(isActive)}>Financiero</NavLink>
            <NavLink to={{ pathname: "/operaciones", search: location.search }} className={linkBase} style={({ isActive }) => estiloLink(isActive)}>Operaciones</NavLink>
            {/* "Administrador" se oculta a quien no sea admin: no se ofrece una
                puerta que va a rebotar. */}
            {esAdmin && (
              <>
                <NavLink to={{ pathname: "/admin", search: location.search }} className={linkBase} style={({ isActive }) => estiloLink(isActive)}>Administrador</NavLink>
                <NavLink to="/admin/usuarios" className={linkBase} style={({ isActive }) => estiloLink(isActive)}>Usuarios</NavLink>
              </>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {user && (
              <span className="text-sm opacity-80" title={user.email}>
                {user.name || user.username}
                <span className="ml-1 opacity-60">({user.role})</span>
              </span>
            )}
            <ThemeToggle />
            <button
              type="button"
              onClick={() => { void logout(); }}
              className="px-3 py-1.5 rounded-md text-sm font-medium border"
              style={{ borderColor: "var(--border, #d1d5db)" }}
            >
              Salir
            </button>
          </div>
        </header>
        <main>
          <Routes>
            <Route path="/" element={<Comercial />} />
            <Route path="/comercial" element={<Comercial />} />
            <Route path="/financiero" element={<Financiero />} />
            <Route path="/operaciones" element={<Operaciones />} />
            <Route path="/admin" element={<RequiereAdmin><Admin /></RequiereAdmin>} />
            <Route path="/admin/usuarios" element={<RequiereAdmin><AdminUsuarios /></RequiereAdmin>} />
            <Route path="*" element={<Comercial />} />
          </Routes>
        </main>
      </div>
    </FilterProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* El login queda FUERA de RequiereLogin, o seria un bucle infinito. */}
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<RequiereLogin><Layout /></RequiereLogin>} />
      </Routes>
    </AuthProvider>
  );
}
